import type React from 'react';

import type { VNFieldHelpProps } from '@/components/VNFieldHelp';

export interface DropdownOption {
  id: string | number;
  label: string;
  depth?: number;
  childCount?: number;
  isLinkable?: boolean;
  icon?: React.ReactNode;
  iconAlign?: 'left' | 'right';
  /** When true, option is shown but cannot be selected. */
  disabled?: boolean;
  // Extra properties for use with filterOptions.propertyPath
  [key: string]: unknown;
}

export interface DisplaySlotProps extends VNFieldHelpProps {
  selectedOptions: DropdownOption[];
  isOpen: boolean;
  onToggle: () => void;
  onClearOption: (id: DropdownOption['id']) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  multiple?: boolean;
  inputRef?: React.Ref<HTMLButtonElement>;
  triggerId: string;
  listboxId: string;
  focusedOptionId?: string;
  onKeyDown: React.KeyboardEventHandler<HTMLButtonElement>;
  disableBorder?: boolean;
  compact?: boolean;
  allowClear?: boolean;
  onClearAll?: () => void;
  /** When true, floating label is rendered by VNFieldHelpLayout instead. */
  hideFloatingLabel?: boolean;
}

export interface FilterOption {
  key: string | number;
  label: string;
  propertyPath: string;
}

export interface ListSlotProps extends Pick<VNFieldHelpProps, 'dropDownItemHelperTooltip' | 'dropDownItemHelperText'> {
  options: DropdownOption[];
  selectedIds: Array<DropdownOption['id']>;
  onSelect: (option: DropdownOption) => void;
  onClose: () => void;
  multiple?: boolean;
  focusedIndex: number;
  listboxId: string;
  optionIdPrefix: string;
  insetQuery?: string;
  filterOptions?: FilterOption[];
  onSelectAll?: (options: DropdownOption[]) => void;
  isLiveLoading?: boolean;
}

/** Filters options by label query, preserving ancestor nodes for tree integrity. */
export function filterOptionsWithAncestors(options: DropdownOption[], query: string): DropdownOption[] {
  if (!query.trim()) return options;
  const q = query.toLowerCase();

  const matchingIndices = new Set<number>();
  options.forEach((o, i) => {
    if (o.label.toLowerCase().includes(q)) matchingIndices.add(i);
  });
  if (matchingIndices.size === 0) return [];

  const includeIndices = new Set<number>(matchingIndices);
  matchingIndices.forEach((idx) => {
    let currentDepth = options[idx].depth ?? 0;
    for (let i = idx - 1; i >= 0 && currentDepth > 0; i--) {
      const ancestorDepth = options[i].depth ?? 0;
      if (ancestorDepth < currentDepth) {
        includeIndices.add(i);
        currentDepth = ancestorDepth;
      }
    }
  });

  return options.filter((_, i) => includeIndices.has(i));
}

export interface WithMinSelections {
  minSelections?: number;
}
